import { DateFormatOptions, TimeFormatOptions } from './date-format-options';
import { FilterDocument, GoalFilterDocument } from './filter-document';
import { Goal } from './goal';
import { GoalOverviewRoadmapViewOptions } from './goal-overview';
import { ID } from './id';
import { Insights, InsightsTimeframes } from './insights';
import { LifeArea } from './life-area';
import { Locales } from './locales';
import { NumberFormatOptions } from './number-format-options';
import { DayPartSections, TypeSections } from './page-sections';
import {
  GoalGroupBy,
  GoalSortingMode,
  GroupByMode,
  HabitGroupBy,
  HabitSortingMode,
  OrderDirection,
  SortingMode,
  TodayGroupBy,
} from './sorting-mode';
import { Tag } from './tag';
import { Task } from './task';
import { TaskPriorityOptions } from './task-base';
import { ThemeModes } from './theme-modes';
import { TimelineTimeframes } from './timeline';
import {
  CalendarViewOptions,
  UpcomingViewOptions,
} from './upcoming-view-options';
import { WeekDays } from './week-days';

export type Notifications = {
  taskReminder?: {
    desktop?: boolean;
    mobile?: boolean;
  };
  taskSummary?: {
    desktop?: boolean;
    mobile?: boolean;
  };
  weeklyReview?: {
    desktop?: boolean;
    mobile?: boolean;
  };
};

export enum FavoriteType {
  Task = 'task',
  Goal = 'goal',
  LifeArea = 'lifeArea',
  Tag = 'tag',
}

export type FavoriteReference = {
  type: FavoriteType;
  id: ID;
};

export type Favorite =
  | {
      id: ID;
      type: FavoriteType.Task;
      value: Task;
    }
  | {
      id: ID;
      type: FavoriteType.Goal;
      value: Goal;
    }
  | {
      id: ID;
      type: FavoriteType.LifeArea;
      value: LifeArea;
    }
  | {
      id: ID;
      type: FavoriteType.Tag;
      value: Tag;
    };

export enum GoalRoadmapSortingKeys {
  InProgress = 'inProgress',
  Unplanned = 'unplanned',
  Completed = 'completed',
  Archived = 'archived',
}

export type Year = string;

export type GoalRoadmapSortingOptions = GoalRoadmapSortingKeys | Year;

export enum GoalRoadmapSortingModes {
  CreatedAt = 'createdAt',
  Custom = 'custom',
}

export type GoalTabSorting = {
  mode: GoalRoadmapSortingModes;
  sorting: ID[];
};

export type UserSettings = {
  language?: Locales;
  country?: string;
  dateFormat?: DateFormatOptions;
  numberFormat?: NumberFormatOptions;
  timeFormat?: TimeFormatOptions;
  startOfWeekIsIosSystemManaged?: boolean;
  startOfWeek?: WeekDays;
  notifications?: Notifications;
  themePreference?: {
    desktop?: ThemeModes;
    mobile?: ThemeModes;
  };
  favorites?: FavoriteReference[];
  webSideNavigationClosed?: boolean;
  smartListsSectionOpen?: boolean;
  isTaskNotificationEnabled?: boolean;
  isCheckInNotificationEnabled?: boolean;
  isHabitNotificationEnabled?: boolean;
  isAppLockEnabled?: boolean;
  insights?: Insights;
  todoSortingMode?: SortingMode;
  todayGroupBy?: TodayGroupBy;
  todayGroupSorting?: {
    dayPart?: {
      sorting: Partial<Record<DayPartSections, ID[]>>;
    };
    goal?: {
      // goalId: [task.id]
      sorting: Partial<Record<ID, ID[]>>;
    };
    lifeArea?: {
      // lifeAreaId: [task.id]
      sorting: Partial<Record<ID, ID[]>>;
    };
    priority?: {
      // high: [task.id]
      sorting: Partial<Record<TaskPriorityOptions, ID[]>>;
    };
    type?: {
      // high: [task.id]
      sorting: Partial<Record<TypeSections, ID[]>>;
    };
  };
  todayHideHabits?: boolean;
  todayHideExternalCalendarEvents?: boolean;
  todayHideDesktopExternalCalendarEvents?: boolean;
  todayFilterByTags?: ID[];
  today?: {
    sidePanelOpen?: boolean;
    sidePanelWidth?: number;
  };
  upcoming?: {
    hideCompleted?: boolean;
    hideHabits?: boolean;
    hideExternalCalendarEvents?: boolean;
    sidePanelOpen?: boolean;
    sidePanelWidth?: number;
    webView?: UpcomingViewOptions;
    webCalendarView?: CalendarViewOptions;
    calendarList?: string;
    allTasks?: {
      openedSections?: ID[];
      hideHabits?: boolean;
      hideUnplannedGoals?: boolean;
      filterByTags?: ID[];
      hideEmptyLifeAreas?: boolean;
    };
  };
  calendar?: {
    hideHabits?: boolean;
    hideCompletedTasks?: boolean;
    hideExternalCalendarEvents?: boolean;
    numberOfDays?: number;
  };
  goalsOverview?: {
    webView?: GoalOverviewRoadmapViewOptions;
    roadmap?: {
      hideUnplannedGoals?: boolean;
      hideCompletedGoals?: boolean;
      hideArchivedGoals?: boolean;
      showEmptyLifeAreas?: boolean;
      collapsedItems?: ID[];
      timeframe?: TimelineTimeframes;
      showCompletedGoals?: boolean;
    };
  };
  goalDetail?: {
    roadmap?: {
      showCompletedGoals?: boolean;
    };
  };
  lifeAreaDetail?: {
    goals?: {
      showCompletedGoals?: boolean;
    };
  };
  goalGroupSorting?: {
    lifeArea?: {
      sorting: Record<ID, ID[]>;
    };
  };
  todoSorting?: Record<string, ID[]>; // { '2024-12-31': ['task-id-1'], ... }
  inboxSorting?: ID[];
  inboxFilterByTags?: ID[];
  habitSorting?: ID[] | null;
  habitStatisticsTimeframe?: InsightsTimeframes;
  habitGroupBy?: HabitGroupBy;
  habitSortingMode?: HabitSortingMode;
  habitGroupSorting?: {
    goal?: {
      // goalId: [task.id]
      sorting: Record<ID, ID[]>;
    };
    lifeArea?: {
      // lifeAreaId: [task.id]
      sorting: Record<ID, ID[]>;
    };
    priority?: {
      // high: [task.id]
      sorting: Record<TaskPriorityOptions, ID[]>;
    };
  };
  allTasks?: {
    unlinkedPlannedTasksSorting?: ID[];
    collapsedGoals?: ID[];
    collapsedLifeAreas?: ID[];
    filterByTags?: ID[] | null;
    hideHabits?: boolean | null;
  };
  goalGroupBy?: GoalGroupBy;
  goalSortingMode?: GoalSortingMode;
  goalSortDirection?: OrderDirection;
  goalSorting?: ID[];
  goalTabSorting?: Record<GoalRoadmapSortingOptions, GoalTabSorting>;
  goalRoadmapSorting?: Record<GoalRoadmapSortingOptions, ID[]>;
  goalRoadmapSortingMode?: GoalSortingMode;
  lifeAreaSorting?: ID[];
  tagSorting?: ID[];
  smartListSorting?: ID[];
  filters?: {
    today?: FilterDocument;
    allTasks?: FilterDocument;
    inbox?: FilterDocument;
    goalsOverview?: GoalFilterDocument;
  };
  customTaskOrder?: CustomTaskOrder;
};

export type CustomTaskOrderSlot = {
  [GroupByMode.None]?: ID[];
} & Partial<Record<Exclude<GroupByMode, GroupByMode.None>, Record<ID, ID[]>>>;

export type CustomTaskOrder = {
  today?: CustomTaskOrderSlot;
  allTasks?: CustomTaskOrderSlot;
  inbox?: CustomTaskOrderSlot;
};
